import toast from 'react-hot-toast';

class TournamentNotifications {
  constructor() {
    this.recentSkinWins = new Set();
    this.notificationTimeout = 3000; // 3 seconds
  }

  /**
   * Show toast notification for skin win
   * @param {Object} skinWin - Skin win data
   * @param {string} skinWin.playerName - Player name
   * @param {number} skinWin.round - Round number
   * @param {number} skinWin.hole - Hole number
   */
  showSkinWinNotification(skinWin) {
    const { playerName, round, hole } = skinWin;
    
    // Create unique key for this skin win to prevent duplicates
    const skinWinKey = `${playerName}-R${round}H${hole}`;
    
    // Check if we've already shown this notification recently
    if (this.recentSkinWins.has(skinWinKey)) {
      return;
    }
    
    // Add to recent notifications
    this.recentSkinWins.add(skinWinKey);
    
    // Remove from recent notifications after timeout
    setTimeout(() => {
      this.recentSkinWins.delete(skinWinKey);
    }, this.notificationTimeout);
    
    // Show toast notification
    const message = `${playerName} won a skin on R${round} H${hole}`;
    
    toast.success(message, {
      duration: 4000,
      position: 'top-right',
      style: {
        background: '#10b981',
        color: 'white',
        fontWeight: '600',
        fontSize: '14px',
        padding: '12px 16px',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
      },
      icon: '🏆',
      iconTheme: {
        primary: 'white',
        secondary: '#10b981'
      }
    });
  }

  /**
   * Show notification for tournament status change
   * @param {string} tournamentName - Tournament name
   * @param {string} status - New status
   */
  showTournamentStatusNotification(tournamentName, status) {
    const message = `${tournamentName} is now ${status}`;
    
    toast.info(message, {
      duration: 3000,
      position: 'top-right',
      style: {
        background: '#3b82f6',
        color: 'white',
        fontWeight: '600',
        fontSize: '14px',
        padding: '12px 16px',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
      },
      icon: 'ℹ️'
    });
  }

  /**
   * Show notification for round/hole update
   * @param {string} tournamentName - Tournament name
   * @param {number} round - Round number
   * @param {number} hole - Hole number
   */
  showRoundUpdateNotification(tournamentName, round, hole) {
    const message = `${tournamentName} - Now playing R${round} H${hole}`;
    
    toast(message, {
      duration: 2000,
      position: 'top-right',
      style: {
        background: '#6b7280',
        color: 'white',
        fontWeight: '500',
        fontSize: '13px',
        padding: '10px 14px',
        borderRadius: '6px',
        boxShadow: '0 2px 8px rgba(107, 114, 128, 0.3)'
      },
      icon: '⛳'
    });
  }

  /**
   * Show error notification
   * @param {string} message - Error message
   */
  showErrorNotification(message) {
    toast.error(message, {
      duration: 4000,
      position: 'top-right',
      style: {
        background: '#ef4444',
        color: 'white',
        fontWeight: '600',
        fontSize: '14px',
        padding: '12px 16px',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
      },
      icon: '❌'
    });
  }

  /**
   * Show loading notification
   * @param {string} message - Loading message
   */
  showLoadingNotification(message) {
    return toast.loading(message, {
      position: 'top-right',
      style: {
        background: '#f59e0b',
        color: 'white',
        fontWeight: '600',
        fontSize: '14px',
        padding: '12px 16px',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)'
      }
    });
  }

  /**
   * Dismiss loading notification
   * @param {string} toastId - Toast ID from showLoadingNotification
   * @param {string} message - Success message
   * @param {string} type - 'success', 'error', or 'info'
   */
  dismissLoadingNotification(toastId, message, type = 'success') {
    toast.dismiss(toastId);
    
    const toastOptions = {
      duration: 3000,
      position: 'top-right',
      style: {
        fontWeight: '600',
        fontSize: '14px',
        padding: '12px 16px',
        borderRadius: '8px'
      }
    };

    switch (type) {
      case 'success':
        toast.success(message, {
          ...toastOptions,
          style: {
            ...toastOptions.style,
            background: '#10b981',
            color: 'white',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
          },
          icon: '✅'
        });
        break;
      case 'error':
        toast.error(message, {
          ...toastOptions,
          style: {
            ...toastOptions.style,
            background: '#ef4444',
            color: 'white',
            boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
          },
          icon: '❌'
        });
        break;
      default:
        toast(message, {
          ...toastOptions,
          style: {
            ...toastOptions.style,
            background: '#3b82f6',
            color: 'white',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
          },
          icon: 'ℹ️'
        });
    }
  }

  /**
   * Clear all notifications
   */
  clearAllNotifications() {
    toast.dismiss();
    this.recentSkinWins.clear();
  }
}

// Create singleton instance
const tournamentNotifications = new TournamentNotifications();

export default tournamentNotifications;
